import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ExternalHyperlink } from 'docx';
import { ApiService } from '../api/api.service';

export interface DocFile {
  id: number;
  title?: string;
  name?: string;
  file_type: number;
  file_url: string;
  content_html?: string;
  contentShowed?: string;
}

export interface DownloadItem {
  id: number;
  name: string;
  status: 'loading' | 'done' | 'error';
}

@Injectable({
  providedIn: 'root'
})
export class DocumentDownloadService {

  /** Téléchargements en cours / récents, affichés par <app-download-indicator>. */
  readonly downloads = signal<DownloadItem[]>([]);
  private downloadSeq = 0;

  constructor(private http: HttpClient, private apiService: ApiService) {}

  download(doc: DocFile): void {
    if (doc.file_type === 4) {
      // Vidéo → ouvrir dans un nouvel onglet
      window.open(doc.file_url, '_blank');
    } else if (doc.file_type === 1) {
      // Document ou image → convertir en DOCX
      this.downloadAsDocx(doc);
    } else {
      // Autres types → télécharger via le back (une requête XHR directe sur l'URL S3
      // est bloquée par CORS, le bucket OVH n'expose pas de règle pour notre origine)
      this.downloadFile(doc);
    }
  }

  private downloadFile(doc: DocFile): void {
    const fileName = this.withExtension(doc.title || `document-${doc.id}`, doc.file_url);
    const request$ = doc.id
      ? this.apiService.getSingleDocument2(doc.id)
      : this.http.get(doc.file_url, { responseType: 'blob' });

    this.trackDownload(fileName, request$);
  }

  /**
   * Suit un téléchargement de blob pour l'indicateur flottant (<app-download-indicator>).
   * Anti-scintillement : l'indicateur n'apparaît que si le téléchargement dépasse
   * ~350 ms ; les erreurs s'affichent toujours.
   */
  trackDownload(fileName: string, request$: Observable<Blob>): void {
    const item: DownloadItem = { id: ++this.downloadSeq, name: fileName, status: 'loading' };
    const showTimer = setTimeout(() => {
      this.downloads.update((list) => [...list, item]);
    }, 350);

    request$.subscribe({
      next: (response: Blob) => {
        clearTimeout(showTimer);
        saveAs(new Blob([response], { type: response.type }), fileName);
        this.finishDownload(item.id, 'done');
      },
      error: (error) => {
        clearTimeout(showTimer);
        console.error('Erreur lors du téléchargement du fichier :', error);
        // Une erreur doit être visible même si elle survient avant le délai d'affichage
        this.downloads.update((list) => list.some((d) => d.id === item.id)
          ? list
          : [...list, item]);
        this.finishDownload(item.id, 'error');
      }
    });
  }

  private finishDownload(id: number, status: DownloadItem['status']): void {
    const removeDelay = status === 'error' ? 4000 : 2000;
    this.downloads.update((list) => list.map((d) => d.id === id ? { ...d, status } : d));
    setTimeout(() => {
      this.downloads.update((list) => list.filter((d) => d.id !== id));
    }, removeDelay);
  }

  /** Récupère l'extension depuis l'URL du fichier si le nom n'en a pas. */
  private withExtension(fileName: string, fileUrl: string): string {
    if (/\.[^/.]+$/.test(fileName)) return fileName;
    const ext = fileUrl.match(/\.([^./?]+)(?:\?|$)/)?.[1];
    return ext ? `${fileName}.${ext}` : fileName;
  }

  downloadAsDocx(doc: DocFile): void {
    // const html = doc.content_html || doc.contentShowed || '<p> </p>';
    let html = doc.content_html || doc.contentShowed;
    if (!html || html.trim() === '') {
      this.apiService.getDocumentContent(doc.id).subscribe({
        next: (response: { content_html: string }) => {
          html = response.content_html || '<p> </p>';

          const docxDocument = this.parseHtmlToDocx(doc.title || 'document', html);

          Packer.toBlob(docxDocument).then(blob => {
            const title = (doc.title || 'document').replace(/\.[^/.]+$/, '');
            saveAs(blob, `${title}.docx`);
          });
        },
        error: (error) => {
          console.error('Erreur lors de la récupération du contenu du document :', error);
          html = '<p> </p>';
        }
      });
    } else {
      const docxDocument = this.parseHtmlToDocx(doc.title || 'document', html);

      Packer.toBlob(docxDocument).then(blob => {
        const title = (doc.title || 'document').replace(/\.[^/.]+$/, '');
        saveAs(blob, `${title}.docx`);
      });
    }

    

  }

  private parseHtmlToDocx(title: string, html: string): Document {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const paragraphs: Paragraph[] = [];

    function parseElement(el: Element) {
      const tag = el.tagName;
      if (["P","H1","H2","H3","H4","H5","H6"].includes(tag)) {
        const headingMap = {
          H1: HeadingLevel.HEADING_1,
          H2: HeadingLevel.HEADING_2,
          H3: HeadingLevel.HEADING_3,
          H4: HeadingLevel.HEADING_4,
          H5: HeadingLevel.HEADING_5,
          H6: HeadingLevel.HEADING_6
        };
        const runs = parseNode(el).map(r => {
          if ((r as any).hyperlink) {
            return new ExternalHyperlink({
              children: [new TextRun({
                text: r.text,
                bold: r.bold,
                italics: r.italics,
                underline: r.underline ? {} : undefined,
                color: r.color
              })],
              link: (r as any).hyperlink
            }) as any;
          } else {
            return new TextRun({
              text: r.text,
              bold: r.bold,
              italics: r.italics,
              underline: r.underline ? {} : undefined,
              color: r.color
            });
          }
        });
        paragraphs.push(new Paragraph({
          children: runs,
          heading: headingMap[tag as keyof typeof headingMap]
        }));
      } else if (tag === "OL" || tag === "UL") {
        Array.from(el.children).forEach(li => {
          const runs = parseNode(li).map(r => new TextRun({
            text: r.text,
            bold: r.bold,
            italics: r.italics,
            underline: r.underline ? {} : undefined,
            color: r.color
          }));
          paragraphs.push(new Paragraph({
            children: runs,
            numbering: { reference: tag === "OL" ? "numbered-list" : "bullet-list", level: 0 }
          }));
        });
      } else {
        el.childNodes.forEach(child => {
          if (child.nodeType === Node.ELEMENT_NODE) parseElement(child as Element);
          else if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
            paragraphs.push(new Paragraph({ children: [new TextRun({ text: child.textContent })] }));
          }
        });
      }
    }

    doc.body.childNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) parseElement(node as Element);
      else if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        paragraphs.push(new Paragraph({ children: [new TextRun({ text: node.textContent })] }));
      }
    });

    return new Document({
      numbering: {
        config: [
          { reference: "numbered-list", levels: [{ level: 0, format: "decimal", text: "%1.", alignment: AlignmentType.START }] },
          { reference: "bullet-list", levels: [{ level: 0, format: "bullet", text: "•", alignment: AlignmentType.START }] }
        ]
      },
      sections: [{ children: paragraphs }]
    });
  }
}

function rgbToHex(rgb: string) {
  const result = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/.exec(rgb);
  return result
    ? ((1 << 24) + (parseInt(result[1]) << 16) + (parseInt(result[2]) << 8) + parseInt(result[3]))
      .toString(16)
      .slice(1)
    : rgb;
}

interface RunStyle {
  text: string;
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  color?: string;
  hyperlink?: string;
}

function parseNode(node: ChildNode): RunStyle[] {
  const runs: RunStyle[] = [];
  node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent?.trim()) runs.push({ text: child.textContent });
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as HTMLElement;
      const color = el.style.color ? rgbToHex(el.style.color) : undefined;
      const bold = el.tagName === "B" || el.tagName === "STRONG";
      const italics = el.tagName === "I" || el.tagName === "EM";
      const underline = el.tagName === "U";
      if (el.tagName === "A" && el.getAttribute("href")) {
        runs.push({
          text: el.textContent || "",
          color: color || "0000FF",
          bold,
          italics,
          underline,
          hyperlink: el.getAttribute("href")!
        } as any);
      } else {
        const childRuns = parseNode(el).map(r => ({
          text: r.text,
          bold: bold || r.bold,
          italics: italics || r.italics,
          underline: underline || r.underline,
          color: color || r.color
        }));
        runs.push(...childRuns);
      }
    }
  });
  return runs;
}
